import { IComponent, getV2ValueWP, tryParseJSON } from "@eventr/common";
import { useCallback } from "react";
import { ComponentFinder } from "../componentFinder";

interface IShouldRenderProps {
  data: any;
  renderOptions: IShouldRenderRenderOptions;
  parentData?: any;
  past?: boolean;
}
interface IShouldRenderRenderOptions {
  components: IComponent[];
  conditions: ICondition[];
}
export interface ICondition {
  operator:
    | "equal"
    | "notEqual"
    | "greaterThan"
    | "lessThan"
    | "greaterThanOrEqual"
    | "lessThanOrEqual"
    | "oneNotEmpty"
    | "notEmpty"
    | "empty"
    | "notEmptyOrEmptyList"
    | "inList"
    | "labelIncludes";
  params: string[];
}
export const ShouldRender = ({ data, renderOptions, parentData, past }: IShouldRenderProps) => {
  const shouldRender = useCallback(() => {
    let shouldRender = true;

    const checkPair = (params: string[]): [string, string] | undefined => {
      const [p0, p1] = params;
      return p0 !== undefined && p1 !== undefined ? [p0, p1] : undefined;
    };
    const failsPair = (params: string[], fails: (a: any, b: any) => boolean) => {
      const pair = checkPair(params);
      if (!pair) return true;
      return fails(getV2ValueWP(pair[0], data), getV2ValueWP(pair[1], data));
    };

    renderOptions.conditions.forEach((c) => {
      switch (c.operator) {
        case "equal":
          if (failsPair(c.params, (a, b) => a !== b)) {
            shouldRender = false;
          }
          break;
        case "notEqual": {
          const p0 = c.params[0];
          if (p0 === undefined) {
            shouldRender = false;
            break;
          }
          const paramsArray = c.params.slice(1);
          const paramsResolvedArray = paramsArray.map((o) => {
            return getV2ValueWP(o, data).toString();
          });
          if (paramsResolvedArray.includes(getV2ValueWP(p0, data))) {
            shouldRender = false;
          }
          break;
        }
        case "greaterThan":
          if (failsPair(c.params, (a, b) => a < b)) {
            shouldRender = false;
          }
          break;
        case "lessThan":
          if (failsPair(c.params, (a, b) => a > b)) {
            shouldRender = false;
          }
          break;
        case "greaterThanOrEqual":
          if (failsPair(c.params, (a, b) => a <= b)) {
            shouldRender = false;
          }
          break;
        case "lessThanOrEqual":
          if (failsPair(c.params, (a, b) => a >= b)) {
            shouldRender = false;
          }
          break;
        case "oneNotEmpty":
          const onePresent = c.params.some((p) => {
            const val = getV2ValueWP(p, data);
            if (val !== undefined && val !== null && val !== "" && val !== "[]" && val !== "{}") {
              return true;
            }
            return false;
          });
          shouldRender = onePresent;
          break;
        case "notEmpty":
          const res = c.params.some((p) => {
            const val = getV2ValueWP(p, data);
            if (val !== undefined && val !== null && val !== "" && val !== "[]" && val !== "{}") {
              return true;
            }
            return false;
          });
          shouldRender = res;
          break;
        case "empty":
          const empty = c.params.every((p) => {
            if (getV2ValueWP(p, data) === undefined || getV2ValueWP(p, data) === null || getV2ValueWP(p, data) === "") {
              return true;
            }
            return false;
          });
          shouldRender = empty;
          break;
        case "notEmptyOrEmptyList":
          const ans = c.params.some((p) => {
            if (
              getV2ValueWP(p, data) === undefined ||
              getV2ValueWP(p, data) === null ||
              getV2ValueWP(p, data) === "" ||
              getV2ValueWP(p, data) === "[]"
            ) {
              return false;
            }
            return true;
          });
          shouldRender = ans;
          break;
        case "inList": {
          const pair = checkPair(c.params);
          if (!pair) {
            shouldRender = false;
            break;
          }
          const [p0, p1] = pair;
          const list = tryParseJSON(getV2ValueWP(p0, data), []);
          const optionArray = c.params.slice(2);

          const present = list.some((element: any) => {
            const optionResolvedArray = optionArray.map((o) => {
              return getV2ValueWP(o, element).toString();
            });

            return optionResolvedArray.includes(getV2ValueWP(p1, element).toString());
          });
          shouldRender = present;
          break;
        }
        case "labelIncludes":
          shouldRender = false;
          for (const param of c.params) {
            if (data && data.label && data.label.includes(param)) {
              shouldRender = true;
            }
          }
          break;
      }
    });
    return shouldRender;
  }, [renderOptions, data]);

  return (
    <>
      {shouldRender() &&
        renderOptions?.components.map((c, i) => {
          return (
            <ComponentFinder
              key={i}
              data={data}
              renderOptions={c}
              componentKey={c.componentKey}
              parentData={parentData}
              isInPast={past}
            />
          );
        })}
    </>
  );
};
